import { clone } from "lib/clone"
import { buildMacroNameLookup, expandMacrosInSchedule, replaceMacroLabelsWithIds } from "./macro-text-utils"
import { getMacroTokenRanges } from "./macro-token-utils"
import { buildChipHtmlForToken, stripChipHtml } from "./macro-chip-utils"

export function buildScheduleLabel(schedule, macroMap) {
    if (!schedule) return schedule
    if (!hasMacroMap(macroMap)) return clone(schedule)
    return expandMacrosInSchedule(schedule, { macrosById: macroMap ?? {}, mode: "label" })
}

/**
 * Builds a schedule for editing: all token fields render visual chips
 * (including component-ref tokens in plain-text fields like title).
 */
export function buildScheduleForEditing(schedule, macroMap) {
    if (!schedule) return schedule
    if (!hasMacroMap(macroMap)) return clone(schedule)
    const cloned = clone(schedule)
    const macrosById = macroMap ?? {}

    if (cloned.title) {
        cloned.title = wrapTokensAsChipHtml(cloned.title, macrosById)
    }

    if (cloned.intro) {
        cloned.intro = {
            ...cloned.intro,
            content: wrapTokensAsChipHtml(cloned.intro.content, macrosById),
            note: wrapTokensAsChipHtml(cloned.intro.note, macrosById),
        }
    }

    if (Array.isArray(cloned.tasks)) {
        cloned.tasks = cloned.tasks.map((task) => ({
            ...task,
            title: wrapTokensAsChipHtml(task?.title, macrosById),
            content: wrapTokensAsChipHtml(task?.content, macrosById),
            note: wrapTokensAsChipHtml(task?.note, macrosById),
        }))
    }

    return cloned
}

export function buildScheduleContent(schedule, macroMap) {
    if (!schedule) return schedule
    if (!hasMacroMap(macroMap)) return clone(schedule)
    const normalized = normalizeScheduleToMacroIds(schedule, macroMap)
    return buildScheduleContentFromNormalized(normalized, macroMap)
}

export function buildScheduleContentFromNormalized(schedule, macroMap) {
    if (!schedule) return schedule
    if (!hasMacroMap(macroMap)) return clone(schedule)
    return expandMacrosInSchedule(schedule, { macrosById: macroMap ?? {}, mode: "content" })
}

export function normalizeScheduleToMacroIds(schedule, macroMap) {
    if (!schedule) return schedule
    if (!hasMacroMap(macroMap)) return schedule
    const macrosById = macroMap ?? {}
    const macrosByName = buildMacroNameLookup(Object.values(macrosById))
    const converted = replaceMacroLabelsWithIdsInSchedule(schedule, macrosById, macrosByName)
    if (converted?.macroMap) delete converted.macroMap
    return converted
}

/** Normalizes schedule-level macro fields only — skips the task list. */
export function normalizeScheduleMetadataToMacroIds(schedule, macroMap) {
    if (!schedule) return schedule
    if (!hasMacroMap(macroMap)) return schedule

    const macrosById = macroMap ?? {}
    const macrosByName = buildMacroNameLookup(Object.values(macrosById))
    const metadata = {}

    if (schedule.code !== undefined) metadata.code = schedule.code
    if (schedule.unitOfMeasure !== undefined) metadata.unitOfMeasure = schedule.unitOfMeasure

    if (schedule.title !== undefined) {
        metadata.title = schedule.title
            ? replaceMacroLabelsWithIds({
                  textOrHtml: stripChipHtml(schedule.title),
                  macrosById,
                  macrosByName,
              })
            : schedule.title
    }

    if (schedule.intro) {
        metadata.intro = {
            ...schedule.intro,
            content: replaceMacroLabelsWithIds({
                textOrHtml: stripChipHtml(schedule.intro.content),
                macrosById,
                macrosByName,
            }),
            note: replaceMacroLabelsWithIds({
                textOrHtml: stripChipHtml(schedule.intro.note),
                macrosById,
                macrosByName,
            }),
        }
    }

    if (Array.isArray(schedule.legislation)) {
        metadata.legislation = schedule.legislation
    }

    return metadata
}

/** Normalizes macro fields on a single task — skips other schedule fields. */
export function normalizeTaskToMacroIds(task, macroMap) {
    if (!task) return task
    if (!hasMacroMap(macroMap)) return task

    const macrosById = macroMap ?? {}
    const macrosByName = buildMacroNameLookup(Object.values(macrosById))

    return {
        ...task,
        title: replaceMacroLabelsWithIds({
            textOrHtml: stripChipHtml(task?.title),
            macrosById,
            macrosByName,
        }),
        content: replaceMacroLabelsWithIds({
            textOrHtml: stripChipHtml(task?.content),
            macrosById,
            macrosByName,
        }),
        note: replaceMacroLabelsWithIds({
            textOrHtml: stripChipHtml(task?.note),
            macrosById,
            macrosByName,
        }),
    }
}

function replaceMacroLabelsWithIdsInSchedule(schedule, macrosById, macrosByName) {
    if (!schedule) return schedule
    const cloned = clone(schedule)

    if (cloned.title) {
        cloned.title = replaceMacroLabelsWithIds({ textOrHtml: stripChipHtml(cloned.title), macrosById, macrosByName })
    }

    if (cloned.intro) {
        cloned.intro = {
            ...cloned.intro,
            content: replaceMacroLabelsWithIds({
                textOrHtml: stripChipHtml(cloned.intro.content),
                macrosById,
                macrosByName,
            }),
            note: replaceMacroLabelsWithIds({
                textOrHtml: stripChipHtml(cloned.intro.note),
                macrosById,
                macrosByName,
            }),
        }
    }

    if (Array.isArray(cloned.tasks)) {
        cloned.tasks = cloned.tasks.map((task) => ({
            ...task,
            title: replaceMacroLabelsWithIds({
                textOrHtml: stripChipHtml(task?.title),
                macrosById,
                macrosByName,
            }),
            content: replaceMacroLabelsWithIds({
                textOrHtml: stripChipHtml(task?.content),
                macrosById,
                macrosByName,
            }),
            note: replaceMacroLabelsWithIds({
                textOrHtml: stripChipHtml(task?.note),
                macrosById,
                macrosByName,
            }),
        }))
    }

    return cloned
}

function wrapTokensAsChipHtml(textOrHtml, macroMap) {
    if (!textOrHtml) return textOrHtml
    const value = `${textOrHtml}`
    const ranges = getMacroTokenRanges(value)
    if (!ranges.length) return value

    let output = ""
    let cursor = 0
    for (const range of ranges) {
        output += value.slice(cursor, range.start)
        output += buildChipHtmlForToken(range.tokenId, macroMap)
        cursor = range.end
    }
    output += value.slice(cursor)
    return output
}

function hasMacroMap(macroMap) {
    return !!macroMap && Object.keys(macroMap).length > 0
}
